import { TUNABLES } from "../config";
import type { RunState } from "../core";
import type { Showcase, ShowcaseScore } from "../game/showcaseRun";
import { onLanguageChange, phrases } from "../i18n";
import type { PlatformAdapter } from "../platform/adapter";
import type { SeatName } from "../render/seats";
import { byId, make } from "./dom";
import { drawMapInto } from "./heroMap";
import { writePhrases } from "./language";
import { mountNameField, type NameRules } from "./nameField";

/**
 * The front page (B3.1, docs/PLAYTEST-PROTOTYPE-PLAN.md, screen 1). Its markup, style and title
 * are in `index.html` (the title written from `GAME_NAME` by the build), so they paint before any
 * script runs; this fills in the visitor's name and its dice, the day's map, and the Play now
 * button, which stays disabled until it can start the game. The duels' cards are `lobbyCards.ts`
 * (B3.2). L10N1: the page's marked words and the caption follow the language. D-108: with the
 * lobby server the map and the note are those of the company table Play now sits at. The name's
 * field is `nameField.ts` (NM2: a name owned by an account). FP1 (`docs/design/FRONT-PAGE-IDEA.md`):
 * over the map, a battle of two scripts drawn by the game's chunk once it is in, its score, and a
 * click on it is Play now's; under it, the time to the next day's map. No Pixi here.
 */
export interface Landing {
  /**
   * What Play now does, from the seat picked on the two cards it opens (D-203), or the one
   * `seat` named; the button shows it is working until the promise settles.
   */
  onPlay(start: (seat: SeatName) => Promise<void>): void;
  /** G44: Play now pressed on the visitor's behalf, if it works and no way in is taken yet. */
  press(): void;
  /**
   * One way into the game at a time: every button of the page is disabled until `start`
   * settles, the pressed one says it is loading, and a failure gives them all back.
   */
  enter(button: HTMLButtonElement, start: () => Promise<void>): void;
  /** The visitor's name as it stands (B3.5: the room's table shows it). */
  name(): string;
  /**
   * D-108: the company table Play now plays online, its run at tick 0 and its code: the map, its
   * caption and Play now's note are its; undefined gives back the day's map and the offline note.
   */
  online(table: { readonly run: RunState; readonly code: string } | undefined): void;
  /** D-120: the lobby's first word is in, or waited for long enough: the held parts show. */
  settle(): void;
  /**
   * FP1: the wait at the table the visitor sits at (`LobbyCards`' `onCountdown`): the ms to its
   * start, null with none yet, undefined seated nowhere. Over the battle, and on the button that
   * took the way in.
   */
  countdown(remainingMs: number | null | undefined): void;
  /** FP1: the game is about to draw: the battle and the countdown go. */
  quiet(): void;
  /** Takes the page away once the game is on screen. */
  close(): void;
}

/**
 * L10N3: a game that did not start says so, in the page's language, at the foot of the page:
 * a link opened straight into the game would otherwise leave a blank page, and Play now a button
 * that only came back.
 */
export function showStartFailure(): void {
  const notice =
    document.getElementById("start-failed") ??
    document.body.appendChild(make("p", { id: "start-failed" }));
  notice.setAttribute("role", "alert");
  notice.dataset.phrase = "startFailed";
  notice.textContent = phrases().front.startFailed;
}

/**
 * FP1: starts the battle in `box` on `run`, telling each new score; `main.ts`'s `startShowcase`,
 * reached through the game's `import()` so the page carries no Pixi. `wanted` is asked once the
 * game's chunk is in: a battle Play now overtook is never drawn (undefined).
 */
export type StartBattle = (
  box: HTMLElement,
  run: RunState,
  onScore: (score: ShowcaseScore) => void,
  wanted: () => boolean,
) => Promise<Showcase | undefined>;

/** How often the line counting down to the next day's map is written again. */
const NEW_MAP_REFRESH_MS = 30_000;
const MS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const MS_PER_DAY = 24 * MINUTES_PER_HOUR * MS_PER_MINUTE;

/** FP1: the time left until the next day's map, midnight UTC, the day `seedFor` reads. */
export function newMapLine(nowMs: number): string {
  const minutes = Math.ceil((MS_PER_DAY - (nowMs % MS_PER_DAY)) / MS_PER_MINUTE);
  return phrases().front.newMapIn(
    Math.floor(minutes / MINUTES_PER_HOUR),
    minutes % MINUTES_PER_HOUR,
  );
}

/** A run's game time as the HUD's clock writes it, m:ss. */
export function clockOf(seconds: number): string {
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

const SIDES = ["player", "rival"] as const;

interface ScoreBoard {
  show(score: ShowcaseScore): void;
  hide(): void;
  /** Writes the board again in the language of the moment. */
  speak(): void;
}

/**
 * FP1: the battle's score over it (the land and the cities of each kingdom, the clock, a bar of
 * the land each holds) and, once it ended, who won, over the battle.
 */
function mountScore(): ScoreBoard {
  const board = byId("score", HTMLElement);
  const clock = byId("score-clock", HTMLElement);
  const share = byId("score-share", HTMLElement);
  const result = byId("battle-result", HTMLElement);
  const cell = (side: string, part: string): HTMLElement | null =>
    board.querySelector<HTMLElement>(`[data-side="${side}"] .${part}`);
  let last: ShowcaseScore | undefined;
  const write = (): void => {
    const words = phrases().front;
    board.hidden = last === undefined;
    if (last === undefined) {
      result.hidden = true;
      return;
    }
    const now = last;
    SIDES.forEach((side, index) => {
      const kingdom = cell(side, "kingdom");
      const land = cell(side, "land");
      const cities = cell(side, "cities");
      if (kingdom && land && cities) {
        kingdom.textContent = words.kingdoms[side];
        land.textContent = String(now.land[index]);
        land.title = words.land;
        cities.textContent = words.cities(now.cities[index] ?? 0);
      }
    });
    clock.textContent = clockOf(now.seconds);
    const held = now.land[0] + now.land[1];
    share.style.width = `${held === 0 ? 50 : (100 * now.land[0]) / held}%`;
    result.hidden = now.status === "running";
    if (now.status !== "running") {
      const turn = make("span", { className: "your-turn", text: words.yourTurn });
      result.replaceChildren(words.wins[now.status === "won" ? "player" : "rival"], turn);
    }
  };
  return {
    show(score) {
      last = score;
      write();
    },
    hide() {
      last = undefined;
      write();
    },
    speak: write,
  };
}

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

/**
 * FP1: how the wait at a table reads: the big count over the battle and the line on the button.
 * Null: the seat is still coming; zero or less: starting; else the whole seconds left, as m:ss
 * from a minute.
 */
export function waitWords(remainingMs: number | null): {
  readonly label: string;
  readonly count: string;
  readonly button: string;
} {
  const words = phrases().front;
  if (remainingMs === null) {
    return { label: words.takingSeat, count: "", button: words.takingSeat };
  }
  if (remainingMs <= 0) {
    return { label: words.lobby.starting, count: "", button: words.lobby.starting };
  }
  const seconds = Math.ceil(remainingMs / MS_PER_SECOND);
  const time = seconds < SECONDS_PER_MINUTE ? String(seconds) : clockOf(seconds);
  return { label: words.startsIn, count: time, button: words.startingIn(time) };
}

/**
 * FP1: the countdown over the battle while the visitor waits at a table, and the same wait on the
 * button that took the way in; undefined takes it away.
 */
function mountCountdown(): {
  show(remainingMs: number | null | undefined, button: HTMLButtonElement | undefined): void;
} {
  const root = byId("landing", HTMLElement);
  const box = byId("battle-countdown", HTMLElement);
  const label = make("span", { className: "label" });
  const count = make("span", { className: "count" });
  box.replaceChildren(label, count);
  return {
    show(remainingMs, button) {
      root.toggleAttribute("data-counting", remainingMs !== undefined);
      box.hidden = remainingMs === undefined;
      if (remainingMs === undefined) {
        return;
      }
      const words = waitWords(remainingMs);
      label.textContent = words.label;
      count.textContent = words.count;
      count.hidden = words.count === "";
      if (button?.getAttribute("aria-busy") === "true" && button.textContent !== words.button) {
        button.textContent = words.button;
      }
    },
  };
}

/** D-203: a seat's card on the front page: its name over the line that says how it plays. */
function writeSeat(button: HTMLButtonElement, title: string, line: string): void {
  const name = button.querySelector(".seat-title");
  const words = button.querySelector(".seat-line");
  if (name && words) {
    name.textContent = title;
    words.textContent = line;
  }
}

/** A draw in [0, 1) for a new name: the visitor's, not the run's, so it comes from crypto, not the run's PRNG. */
export function nameDraw(): number {
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return (values[0] ?? 0) / 2 ** 32;
}

export function mountLanding(options: {
  readonly adapter: PlatformAdapter;
  readonly run: RunState;
  /** The words under the day's map, in the language of the moment. */
  readonly caption: () => string;
  /** NM2: the names accounts own; absent where the page's or the Worker's switch is off. */
  readonly names?: NameRules;
  /**
   * D-120: a lobby server is asked: the map, its caption, the duels and Play now's note stay held
   * unseen (`index.html` paints them so) until `settle`, so the day's map and the local duels do
   * not show and then give way to the server's company map and duels. False shows them at once.
   */
  readonly waitsForLobby: boolean;
  /**
   * FP1: the battle over the map, once the game's chunk is in (`startShowcase` of `main.ts`);
   * absent, the map stays still (`?showcase=off`, the e2e build).
   */
  readonly battle?: StartBattle;
  /** FP1: the map is the day's, so the page counts down to the next one (midnight UTC, `seedFor`). */
  readonly daily: boolean;
  /** G44: the seat a room's Change seat picked: Play now plays it without the cards. */
  readonly seat?: SeatName;
  /** D-203: a seat picked on Play now's cards. */
  readonly chosen?: (seat: SeatName) => void;
}): Landing {
  const root = byId("landing", HTMLElement);
  root.toggleAttribute("data-waiting", options.waitsForLobby);
  const nameInput = byId("player-name", HTMLInputElement);
  const play = byId("play-now", HTMLButtonElement);
  const hero = byId("hero-map", HTMLCanvasElement);
  const caption = byId("hero-caption", HTMLElement);
  const note = byId("play-note", HTMLElement);
  const pick = byId("seat-pick", HTMLElement);
  const pickTitle = byId("seat-pick-title", HTMLElement);
  const seatButtons: Readonly<Record<SeatName, HTMLButtonElement>> = {
    king: byId("seat-king", HTMLButtonElement),
    soldier: byId("seat-soldier", HTMLButtonElement),
  };
  const score = mountScore();
  const newMap = byId("new-map", HTMLElement);
  /** The map Play now plays and its words: the day's, or the company table's (D-108). */
  let shown: { readonly run: RunState; readonly caption: () => string } = options;
  let shownCode: string | undefined;
  const tellNewMap = (): void => {
    newMap.hidden = !options.daily || shownCode !== undefined;
    newMap.textContent = newMapLine(Date.now());
  };
  const speak = (): void => {
    writePhrases(root);
    const seats = phrases().common.seats;
    pickTitle.textContent = seats.choose;
    writeSeat(seatButtons.king, seats.king, seats.kingLine);
    writeSeat(seatButtons.soldier, seats.soldier, seats.soldierLine);
    caption.textContent = shown.caption();
    score.speak();
    tellNewMap();
  };
  speak();
  const unheard = onLanguageChange(speak);
  const ticking = setInterval(tellNewMap, NEW_MAP_REFRESH_MS);

  // The name: the kept one, else a new one; a generated one follows the language.
  nameInput.maxLength = TUNABLES.landing.nameMaxLength;
  const nameField = mountNameField({
    input: nameInput,
    roll: byId("name-roll", HTMLButtonElement),
    note: document.getElementById("name-note"),
    data: options.adapter.data,
    draw: nameDraw,
    ...(options.names === undefined ? {} : { names: options.names }),
  });

  // The day's map, redrawn whenever its box changes size.
  const draw = (): void =>
    drawMapInto(hero, shown.run, TUNABLES.landing.heroPaddingPx, TUNABLES.landing.heroSeamPx);
  draw();
  const resize = new ResizeObserver(() => draw());
  resize.observe(hero);

  // FP1: the battle plays the map Play now plays, from the lobby's first word on (D-120).
  const live = byId("hero-live", HTMLElement);
  let battle: Promise<Showcase | undefined> | undefined;
  let settled = !options.waitsForLobby;
  /** A way into the game was taken (Play now, a card): it plays on until the game is drawn (`quiet`). */
  let entering = false;
  /** The button that took it, which says how the wait goes. */
  let enteredBy: HTMLButtonElement | undefined;
  const startBattle = (): void => {
    const begin = options.battle;
    if (!begin || battle || !settled) {
      return;
    }
    const mine: Promise<Showcase | undefined> = begin(
      live,
      shown.run,
      (now) => {
        if (battle === mine) {
          score.show(now);
          root.toggleAttribute("data-ended", now.status !== "running");
        }
      },
      // A way into the game taken before the chunk came: no battle is drawn at all.
      () => battle === mine && !entering,
    )
      .then((started) => {
        // The page may have been left while the chunk came: the battle stops unseen.
        if (battle === mine) {
          root.toggleAttribute("data-live", true);
        }
        return started;
      })
      .catch((error: unknown) => {
        // No WebGL, or the game's chunk did not come: the day's map stays, still.
        console.warn("the front page's battle did not start", error);
        return undefined;
      });
    battle = mine;
  };
  const stopBattle = (): void => {
    void battle?.then((started) => started?.stop());
    battle = undefined;
    root.removeAttribute("data-live");
    root.removeAttribute("data-ended");
    score.hide();
  };
  startBattle();
  // The battle is a way in too: a click on it is Play now's.
  live.parentElement?.addEventListener("click", () => {
    if (!play.disabled) {
      play.click();
    }
  });

  const countdown = mountCountdown();
  const enter = (button: HTMLButtonElement, start: () => Promise<void>): void => {
    if (entering) {
      return;
    }
    // The battle plays on through a table's countdown; `quiet` stops it before the game draws.
    entering = true;
    enteredBy = button;
    const buttons = [...root.querySelectorAll("button")].filter((each) => !each.disabled);
    const label = button.textContent ?? "";
    for (const each of buttons) {
      each.disabled = true;
    }
    button.setAttribute("aria-busy", "true");
    button.textContent = phrases().front.loading;
    start().catch((error: unknown) => {
      console.error("the game did not start", error);
      showStartFailure();
      entering = false;
      enteredBy = undefined;
      countdown.show(undefined, undefined);
      for (const each of buttons) {
        each.disabled = false;
      }
      button.removeAttribute("aria-busy");
      button.textContent = label;
      startBattle();
    });
  };

  /** D-203: the cards stand in Play now's place until a seat is picked; the wait shows on Play now. */
  const asking = (open: boolean): void => {
    pick.hidden = !open;
    play.hidden = open;
  };
  return {
    onPlay(start) {
      const named = options.seat;
      play.addEventListener("click", () => {
        if (named) {
          enter(play, () => start(named));
          return;
        }
        asking(true);
        seatButtons.king.focus();
      });
      for (const seat of ["king", "soldier"] as const) {
        seatButtons[seat].addEventListener("click", () => {
          if (entering) {
            return;
          }
          options.chosen?.(seat);
          asking(false);
          enter(play, () => start(seat));
        });
      }
      play.disabled = false;
      play.dataset.ready = "true";
      // The exit check of B3.1 reads this mark: Play now can start the game from here on.
      performance.mark("play-ready");
    },
    press() {
      if (!play.disabled) {
        play.click();
      }
    },
    enter,
    name: () => nameField.name(),
    online(table) {
      if (table?.code === shownCode) {
        return;
      }
      shownCode = table?.code;
      shown = table
        ? { run: table.run, caption: () => phrases().front.companyMap(table.code) }
        : options;
      note.dataset.phrase = table ? "playNoteOnline" : "playNote";
      hero.dataset.phraseLabel = table ? "companyMapLabel" : "todaysMapLabel";
      draw();
      speak();
      void battle?.then((started) => started?.play(shown.run));
    },
    settle() {
      root.removeAttribute("data-waiting");
      settled = true;
      if (!entering) {
        startBattle();
      }
    },
    countdown(remainingMs) {
      countdown.show(remainingMs, enteredBy);
    },
    quiet() {
      countdown.show(undefined, undefined);
      stopBattle();
    },
    close() {
      unheard();
      clearInterval(ticking);
      stopBattle();
      nameField.close();
      resize.disconnect();
      root.remove();
    },
  };
}
